// Session BBH: WHO A JOB REPORT GOES TO, chosen by the person pressing Send, and a report SENT OUTSIDE GUVNOR.
//
// ReportSendModal is the office's one way to send a service record, call-out report or repair report. It opens on the
// server's own preview (the address on file, the secondary contact copied), lets the person change To and add Cc, and
// asks the server again before anything goes, so the confirm names EVERY address and the From exactly as the server will
// use them (the AEH branded-confirm rule). The send itself is a two-step ConfirmButton. The server refuses an invalid
// address, ELE's own, an erased person's and more than six; the refusal is shown here word for word.
//
// SentOutsideModal records that a person sent the report from Gmail by hand: their date and time, the addresses they
// type and an optional note. It sends nothing. The suggestions offered in both are the customer's billing and secondary
// addresses and the call-out caller's (from the server's preview): never the Contact list (a contact is a record, not a
// recipient: Session ASN).
import { useEffect, useState } from "react";
import { ApiError, api, type ReportSendPreview } from "../../lib/api";
import { Button } from "../../components/Button";
import { ConfirmButton } from "../../components/ConfirmButton";
import { MODAL_ACTIONS, MODAL_ACTION_BTN, ModalShell } from "../../components/ModalShell";

/** "a@x.com, b@y.com" into a list. Pure. */
export function addressesFrom(text: string): string[] {
  return text
    .split(/[,;\s]+/)
    .map((a) => a.trim())
    .filter((a) => a.length > 0);
}

const FIELD = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";
const LABEL = "mb-1 block text-xs font-menu uppercase tracking-wide text-ele-body";

function errorText(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message || err.code : fallback;
}

export function ReportSendModal({
  jobId,
  source,
  onClose,
  onSent,
}: {
  jobId: string;
  /** "fresh" renders the record now (Send); "filed" re-sends the PDF filed at approval (Resend email). */
  source: "fresh" | "filed";
  onClose: () => void;
  onSent: (res: { sentToClientAt: string | null; to: string[]; cc: string[] }) => void;
}) {
  const [toText, setToText] = useState("");
  const [ccText, setCcText] = useState("");
  const [sendAgain, setSendAgain] = useState(false);
  const [plan, setPlan] = useState<ReportSendPreview | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [checked, setChecked] = useState<ReportSendPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const preview = (body: { to?: string[]; cc?: string[] }) =>
    source === "filed" ? api.jobsheets.resendPreview(jobId, body) : api.jobsheets.sendToClientPreview(jobId, body);

  useEffect(() => {
    let live = true;
    preview({})
      .then((p) => {
        if (!live) return;
        setPlan(p);
        setSuggestions(p.suggestions ?? []);
        setToText(p.to.join(", "));
        setCcText(p.cc.join(", "));
      })
      .catch((err) => live && setError(errorText(err, "The recipients could not be loaded. Type the address to send it to.")))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
    // The preview is asked once, on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, source]);

  async function review() {
    setBusy(true);
    setError(null);
    setChecked(null);
    try {
      setChecked(await preview({ to: addressesFrom(toText), cc: addressesFrom(ccText) }));
    } catch (err) {
      setError(errorText(err, "The recipients could not be checked. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    if (!checked) return;
    setBusy(true);
    setError(null);
    try {
      if (source === "filed") {
        const r = await api.jobsheets.resend(jobId, { to: checked.to, cc: checked.cc });
        onSent({ sentToClientAt: null, to: r.to ?? checked.to, cc: r.cc ?? checked.cc });
      } else {
        const r = await api.jobsheets.sendToClient(jobId, { to: checked.to, cc: checked.cc, ...(sendAgain ? { sendAgain: true } : {}) });
        if (r.alreadySent) {
          setError("This record was already sent and has not changed since, so nothing was sent. Tick 'Send it again' to send it anyway.");
          return;
        }
        onSent({ sentToClientAt: r.sentToClientAt, to: r.to ?? checked.to, cc: r.cc ?? checked.cc });
      }
      onClose();
    } catch (err) {
      setError(errorText(err, "Could not send the report. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  const addTo = (a: string) => {
    const list = addressesFrom(toText);
    if (!list.includes(a)) setToText([...list, a].join(", "));
    setChecked(null);
  };
  const addCc = (a: string) => {
    const list = addressesFrom(ccText);
    if (!list.includes(a)) setCcText([...list, a].join(", "));
    setChecked(null);
  };
  const documentName = plan?.document ?? "report";
  const alreadySent = plan?.alreadySent === true && source === "fresh";

  return (
    <ModalShell ariaLabel="Send the report" onClose={() => !busy && onClose()} width="max-w-lg" panelClassName="p-6">
      <h2 className="mb-1 font-heading text-lg text-ele-strong">Send the {documentName}</h2>
      <p className="mb-4 text-sm text-ele-body">Choose who it goes to. Nothing is sent until you confirm the addresses below.</p>

      {loading ? (
        <p className="text-sm text-ele-body">Loading the address on file...</p>
      ) : (
        <>
          <label htmlFor="report-send-to" className={LABEL}>
            To
          </label>
          <input id="report-send-to" value={toText} onChange={(e) => (setToText(e.target.value), setChecked(null))} className={FIELD} placeholder="name@example.com" />
          <label htmlFor="report-send-cc" className={`${LABEL} mt-3`}>
            Cc (optional)
          </label>
          <input id="report-send-cc" value={ccText} onChange={(e) => (setCcText(e.target.value), setChecked(null))} className={FIELD} placeholder="Separate addresses with commas" />
          <p className="mt-1 text-xs text-ele-body opacity-70">Up to six addresses in all.</p>

          {suggestions.length > 0 && (
            <div className="mt-3">
              <p className="mb-1 text-xs text-ele-body">On file for this customer:</p>
              <ul className="flex flex-wrap gap-2">
                {suggestions.map((a) => (
                  <li key={a} className="flex items-center gap-1 rounded-ele border border-ele-purple-accent px-2 py-1 text-xs text-ele-strong">
                    <span>{a}</span>
                    <button type="button" className="text-ele-strong underline" onClick={() => addTo(a)} aria-label={`Add ${a} to To`}>
                      To
                    </button>
                    <button type="button" className="text-ele-strong underline" onClick={() => addCc(a)} aria-label={`Add ${a} to Cc`}>
                      Cc
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {alreadySent && (
            <label className="mt-3 flex items-center gap-2 text-sm text-ele-body">
              <input type="checkbox" checked={sendAgain} onChange={(e) => setSendAgain(e.target.checked)} />
              Send it again (it was already sent and has not changed since)
            </label>
          )}

          {checked && (
            <div className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-field p-3 text-sm text-ele-strong" data-testid="report-send-confirm">
              <p>
                <span className="font-semibold">From:</span> {checked.from ?? "the connected mailbox"}
              </p>
              <p>
                <span className="font-semibold">To:</span> {checked.to.join(", ")}
              </p>
              <p>
                <span className="font-semibold">Cc:</span> {checked.cc.length > 0 ? checked.cc.join(", ") : "nobody"}
              </p>
            </div>
          )}
        </>
      )}

      {error && (
        <p role="alert" className="mt-3 text-sm text-ele-danger">
          {error}
        </p>
      )}

      <div className={`mt-5 gap-2 ${MODAL_ACTIONS}`}>
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body disabled:opacity-50 ${MODAL_ACTION_BTN}`}
        >
          Cancel
        </button>
        {checked ? (
          <ConfirmButton
            onConfirm={() => void send()}
            label={`Send to ${checked.to.length + checked.cc.length} ${checked.to.length + checked.cc.length === 1 ? "address" : "addresses"}`}
            confirmLabel={`Send to ${[...checked.to, ...checked.cc].join(", ")}?`}
            busy={busy}
            busyLabel="Sending..."
            disabled={(alreadySent && !sendAgain) || busy}
            className={`rounded-ele bg-ele-green px-4 py-2 font-menu text-xs tracking-wide text-ele-bg ${MODAL_ACTION_BTN}`}
            armedClassName="!bg-ele-green font-semibold"
          />
        ) : (
          <Button onClick={() => void review()} disabled={loading || busy || addressesFrom(toText).length === 0} className={MODAL_ACTION_BTN}>
            {busy ? "Checking..." : "Check recipients"}
          </Button>
        )}
      </div>
    </ModalShell>
  );
}

/** The London wall-clock "now" as the value a datetime-local input takes. */
function localNowValue(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function SentOutsideModal({
  jobId,
  suggestions,
  onClose,
  onSaved,
}: {
  jobId: string;
  suggestions: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [when, setWhen] = useState(localNowValue());
  const [toText, setToText] = useState("");
  const [ccText, setCcText] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const at = new Date(when);
      if (Number.isNaN(at.getTime())) {
        setError("Give the date and time it was sent.");
        return;
      }
      await api.jobsheets.markSentOutside(jobId, { sentAt: at.toISOString(), to: addressesFrom(toText), cc: addressesFrom(ccText), ...(note.trim() ? { note: note.trim() } : {}) });
      onSaved();
      onClose();
    } catch (err) {
      setError(errorText(err, "The send could not be recorded. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Record a report sent outside Guvnor" onClose={() => !busy && onClose()} width="max-w-lg" panelClassName="p-6">
      <h2 className="mb-1 font-heading text-lg text-ele-strong">Sent outside Guvnor</h2>
      <p className="mb-4 text-sm text-ele-body">
        Record that this report was sent from Gmail by hand, so the record stops showing it as unsent. Nothing is sent from here.
      </p>
      <label htmlFor="sent-outside-when" className={LABEL}>
        When it was sent
      </label>
      <input id="sent-outside-when" type="datetime-local" value={when} max={localNowValue()} onChange={(e) => setWhen(e.target.value)} className={FIELD} />
      <label htmlFor="sent-outside-to" className={`${LABEL} mt-3`}>
        Sent to
      </label>
      <input id="sent-outside-to" value={toText} onChange={(e) => setToText(e.target.value)} className={FIELD} placeholder="name@example.com" />
      <label htmlFor="sent-outside-cc" className={`${LABEL} mt-3`}>
        Copied (optional)
      </label>
      <input id="sent-outside-cc" value={ccText} onChange={(e) => setCcText(e.target.value)} className={FIELD} placeholder="Separate addresses with commas" />
      {suggestions.length > 0 && (
        <p className="mt-1 text-xs text-ele-body opacity-70">On file: {suggestions.join(", ")}</p>
      )}
      <label htmlFor="sent-outside-note" className={`${LABEL} mt-3`}>
        Note (optional)
      </label>
      <input id="sent-outside-note" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} className={FIELD} placeholder="For example: sent to the managing agent from Gmail" />

      {error && (
        <p role="alert" className="mt-3 text-sm text-ele-danger">
          {error}
        </p>
      )}
      <div className={`mt-5 gap-2 ${MODAL_ACTIONS}`}>
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body disabled:opacity-50 ${MODAL_ACTION_BTN}`}
        >
          Cancel
        </button>
        <Button onClick={() => void save()} disabled={busy || addressesFrom(toText).length === 0} className={MODAL_ACTION_BTN}>
          {busy ? "Saving..." : "Record as sent"}
        </Button>
      </div>
    </ModalShell>
  );
}
